import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react'
import { adresseNotee, noterAdresse } from './navigation/adresse-courante.js'

/**
 * Routeur client minimal (LOT 5.5).
 *
 * C'était LA cause des saccades : chaque clic de navigation faisait
 * `window.location.assign(...)` — un RECHARGEMENT COMPLET du document
 * (page détruite, bundle re-parsé, tous les fetchs relancés). Ici :
 * pushState + un store abonné — seul le contenu de route change, le shell
 * reste monté.
 *
 * Volontairement petit : pas de bibliothèque, pas de nested routes, pas de
 * data-loaders. Le produit a une vingtaine de routes plates.
 */

let listeners: Array<() => void> = []
let currentPath = window.location.pathname
/**
 * Un compteur de NAVIGATIONS (empilée, remplacée, Retour). Le chemin seul ne
 * suffit pas à un écran dont l'adresse porte l'état : rouvrir
 * « /comptabilite/sources » depuis le menu alors qu'on y est déjà, filtré,
 * ne change pas le chemin — l'écran ne l'apprenait jamais, et l'adresse nue
 * annonçait la liste par défaut au-dessus d'une liste encore filtrée.
 */
let navigations = 0
noterAdresse(window.location.pathname + window.location.search)

/**
 * Le chemin affiché à chaque index d'historique, pour ce document : ce qui
 * permet de savoir si l'entrée PRÉCÉDENTE est déjà la page où l'on veut aller
 * (`revenirOuRemplacer`). Un index jamais vu (rechargement) n'y est pas.
 */
const cheminsParIndex = new Map<number, string>()
/** Le Retour que `revenirOuRemplacer` vient de demander : ni garde, ni scroll restauré. */
let retourAttendu: string | null = null

function emit(): void {
  currentPath = window.location.pathname
  navigations += 1
  cheminsParIndex.set(profondeur, currentPath)
  noterAdresse(window.location.pathname + window.location.search)
  listeners.forEach((l) => l())
}

function abonner(cb: () => void): () => void {
  listeners.push(cb)
  return () => { listeners = listeners.filter((l) => l !== cb) }
}

/**
 * GARDE DE NAVIGATION (LOT 5.5.1) — un écran avec des modifications non
 * enregistrées peut intercepter la navigation : la garde reçoit la
 * destination et rend son verdict. Si elle refuse, c'est ELLE qui reprendra
 * la navigation plus tard (via `navigate(path, { force: true })`) après la
 * décision de l'utilisateur (continuer à modifier / abandonner / enregistrer).
 */
type NavigationGuard = (to: string) => boolean

let guard: NavigationGuard | null = null
export function setNavigationGuard(g: NavigationGuard | null): void { guard = g }

window.addEventListener('popstate', (e) => {
  // Le retour navigateur a DÉJÀ changé l'URL quand popstate arrive : si la
  // garde refuse, on remet l'URL en place — rien n'est perdu silencieusement.
  const target = window.location.pathname
  const attendu = retourAttendu !== null && target === retourAttendu
  retourAttendu = null
  if (!attendu && guard && target !== currentPath && !guard(target)) {
    // L'adresse COMPLÈTE de l'écran resté affiché, paramètres compris
    // (`adresse-courante.ts`) : remettre le seul chemin perdait `?onglet=…`.
    window.history.pushState({ lcIdx: profondeur }, '', adresseNotee() ?? currentPath)
    return
  }
  /* V2.1 — l'index vit dans history.state : BACK **et** FORWARD retombent
     sur la bonne profondeur (l'ancien décrément aveugle comptait un
     forward comme un retour). Une entrée sans état (arrivée externe,
     rechargement d'un vieux document) vaut zéro : le repli canonique
     reste la seule issue — on n'expulse jamais l'utilisateur de Gestion. */
  profondeur = typeof (e.state as { lcIdx?: number } | null)?.lcIdx === 'number'
    ? (e.state as { lcIdx: number }).lcIdx : 0
  // Un retour demandé par `revenirOuRemplacer` se comporte comme une
  // redirection : le scroll reste où l'appelant l'a mis.
  dernierKind = attendu ? 'replace' : 'pop'
  emit()
})

/** Les parcours d'authentification restent des documents séparés : y entrer
 *  ou en sortir passe par un vrai chargement (état neuf garanti). La page
 *  « Autoriser LC Porte » en fait partie : hors du shell, ouverte par l'app. */
const FULL_LOAD_PREFIXES = ['/login', '/session-expired', '/access-denied', '/design/', '/pointeuse', '/porte/connexion']

export function isAuthPath(path: string): boolean {
  return FULL_LOAD_PREFIXES.some((p) => path === p || path.startsWith(p))
}

export function navigate(path: string, opts: { force?: boolean } = {}): void {
  if (!opts.force && guard && !guard(path)) return
  if (isAuthPath(path) || isAuthPath(window.location.pathname)) {
    window.location.assign(path)
    return
  }
  if (path === currentPath + window.location.search) return
  profondeur += 1
  window.history.pushState({ lcIdx: profondeur }, '', path)
  dernierKind = 'push'
  // Mémorise la dernière page pour la préférence « Reprendre là où j'étais ».
  try { localStorage.setItem('gestion.last-route', path.split('?')[0] ?? '/') } catch { /* stockage indisponible */ }
  emit()
}

/**
 * REDIRECTION D'UNE ANCIENNE ADRESSE — sans empiler d'historique.
 *
 * `navigate()` empile : revenir en arrière depuis la destination retomberait
 * sur l'ancienne adresse, qui redirigerait aussitôt — un aller-retour dont on
 * ne sort pas. Une redirection REMPLACE l'entrée courante : après elle,
 * l'ancienne adresse n'a jamais existé dans l'historique.
 *
 * Les liens profonds déjà distribués (notifications, favoris) continuent donc
 * de fonctionner sans piéger le bouton Retour.
 */
export function redirect(path: string): void {
  if (path === currentPath + window.location.search) return
  window.history.replaceState({ lcIdx: profondeur }, '', path)
  dernierKind = 'replace'
  try { localStorage.setItem('gestion.last-route', path.split('?')[0] ?? '/') } catch { /* stockage indisponible */ }
  emit()
}

/*
 * PROFONDEUR DE NAVIGATION INTERNE.
 *
 * `window.history.length` ne dit rien d'utile : il compte aussi les pages
 * visitées AVANT d'entrer dans Gestion. Revenir en arrière depuis la première
 * page ouverte ferait donc sortir de l'application.
 *
 * On compte donc nos propres empilements. Tant qu'il est à zéro, « Retour »
 * n'a aucun antécédent interne et doit se rabattre sur une destination sûre.
 */
let profondeur = typeof (window.history.state as { lcIdx?: number } | null)?.lcIdx === 'number'
  ? (window.history.state as { lcIdx: number }).lcIdx : 0
cheminsParIndex.set(profondeur, currentPath)

/** V2.1 — nature de la dernière navigation : pilote le scroll (§29) —
 *  en haut sur un PUSH, restauré sur un POP, intact sur un REPLACE. */
export type NavKind = 'push' | 'pop' | 'replace'
let dernierKind: NavKind = 'push'
export function navigationKind(): NavKind { return dernierKind }
export function navigationIndex(): number { return profondeur }

/**
 * Retour ARRIÈRE réel, avec un repli quand il n'y a nulle part où revenir.
 *
 * Le fil d'ariane des réglages pointait en dur vers `/settings` : depuis
 * « Soirées → Saisons », « Retour » sautait deux niveaux d'un coup et
 * atterrissait sur l'accueil général. Ce n'était pas un retour, c'était un
 * raccourci vers la racine.
 */
export function goBack(fallback: string): void {
  if (profondeur > 0) { window.history.back(); return }
  navigate(fallback)
}

/**
 * QUITTER UN ÉCRAN DE PASSAGE POUR LA PAGE D'OÙ L'ON VENAIT — sans doubler
 * l'historique (revue du 11/10/2026).
 *
 * L'assistant de clôture s'ouvre presque toujours depuis la fiche de la nuit.
 * Remplacer l'assistant par cette fiche (`redirect`) laissait deux fois la
 * même page dans l'historique : le premier Retour ne faisait rien. Si
 * l'entrée PRÉCÉDENTE est déjà la destination, on y revient ; sinon on
 * remplace l'entrée courante. Comme `redirect`, ni garde ni scroll : c'est
 * l'appelant qui le place. Si le Retour n'aboutit pas, la redirection prend
 * le relais.
 */
export function revenirOuRemplacer(path: string): void {
  const precedent = profondeur > 0 ? cheminsParIndex.get(profondeur - 1) : undefined
  if (precedent !== undefined && !path.includes('?') && precedent === path) {
    retourAttendu = precedent
    window.history.back()
    window.setTimeout(() => {
      if (retourAttendu !== precedent) return
      retourAttendu = null
      if (currentPath !== precedent) redirect(path)
    }, 800)
    return
  }
  redirect(path)
}

export function usePath(): string {
  return useSyncExternalStore(abonner, () => currentPath)
}

/**
 * Change à CHAQUE navigation, même vers le chemin déjà affiché : l'écran dont
 * l'adresse porte l'état relit alors ses paramètres (voir `navigations`).
 * Une réécriture par `remplacerRecherche` n'en est pas une : pas de boucle.
 */
export function useNavigationCount(): number {
  return useSyncExternalStore(abonner, () => navigations)
}

/** Lien interne : un vrai <a> (ouvrable dans un onglet, URL visible), mais
 *  intercepté pour naviguer sans recharger. */
export function Link(props: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { href, onClick, ...rest } = props
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    navigate(href)
  }
  return <a {...rest} href={href} onClick={handle} />
}
